"use client";

import { useEnrollmentRounds } from "@/themes/default/templates/section/enrollment/useEnrollmentRounds";

/**
 * crystal enrollment banner — glass panel with the prism hairline, light
 * display type and pill buttons (CtaSection idiom). Token-only colours, so
 * it follows crystal's dark/light toggle.
 */
export function EnrollmentBanner({
  section,
  currentLanguage = "mm",
}: {
  section: any;
  currentLanguage?: "en" | "mm";
}) {
  const d = useEnrollmentRounds(section, currentLanguage);
  if (!d) return null;

  return (
    <section className="px-4 py-8 sm:px-6 lg:px-8" lang={d.lang === "mm" ? "my" : "en"}>
      <div className="crystal-glass relative mx-auto max-w-5xl overflow-hidden rounded-3xl">
        <div className="h-1 w-full bg-crystal-gradient" aria-hidden="true" />
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-full"
          style={{ background: "radial-gradient(50% 80% at 15% 0%, rgba(124,147,245,0.16), transparent 70%)" }}
          aria-hidden="true"
        />
        {d.rounds.map((r, i) => (
          <div
            key={r.id}
            className="relative flex flex-col gap-5 px-6 py-6 md:flex-row md:items-center md:px-10 md:py-8"
            style={i > 0 ? { borderTop: "1px solid var(--color-border)" } : undefined}
          >
            <div className="min-w-0 flex-1">
              <p className="crystal-eyebrow">{d.title}</p>
              <h2
                className="crystal-display mt-2 text-2xl md:text-3xl"
                style={{ color: "var(--color-foreground)", lineHeight: d.lang === "mm" ? 1.7 : undefined }}
              >
                <span className="crystal-prism-text font-medium">{r.label}</span>
              </h2>
              {r.window && (
                <p className="mt-2 text-sm" style={{ color: "var(--color-muted-foreground)" }}>
                  {d.t.windowPrefix} {r.window}
                </p>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              {r.listHref && (
                <a
                  href={r.listHref}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid var(--color-border)",
                    color: "var(--color-foreground)",
                  }}
                >
                  {d.t.list}
                </a>
              )}
              <a
                href={r.enrollHref}
                className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: "var(--color-primary)", color: "var(--color-primary-foreground)" }}
              >
                {d.t.enroll} <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
